import { InputAdornment, TextField } from "@mui/material";
import { useRef, useState } from "react";
import { get, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isEmptyNumber } from "../../../utils/Methods/Timflow";

interface NumberFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  size?: "small" | "medium";
  placeholder?: string;
  helperText?: string;
  step?: number | "any";
  min?: number;
  unit?: string;
}

export default function NumberField({
  name,
  label,
  disabled,
  size,
  placeholder,
  helperText,
  step = "any",
  min,
  unit,
}: Readonly<NumberFieldProps>) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const value = useWatch({ name });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [badInput, setBadInput] = useState(false);
  const shrink = !!placeholder || !isEmptyNumber(value);

  const invalidMsg = t("calculations.methods.errors.invalid-number");

  const { ref, onChange, ...field } = register(name, {
    valueAsNumber: true,
    validate: () => !inputRef.current?.validity.badInput || invalidMsg,
  });

  const fieldError = get(errors, name)?.message as string | undefined;
  const error = badInput ? invalidMsg : fieldError;

  return (
    <TextField
      label={label}
      type="number"
      size={size}
      disabled={disabled}
      fullWidth
      placeholder={placeholder}
      error={!!error}
      helperText={error ?? helperText}
      slotProps={{
        htmlInput: { step, min },
        inputLabel: shrink ? { shrink: true } : undefined,
        input: unit
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  {t(`generic.units.${unit}`)}
                </InputAdornment>
              ),
            }
          : undefined,
      }}
      inputRef={(el: HTMLInputElement | null) => {
        inputRef.current = el;
        ref(el);
      }}
      onChange={(e) => {
        setBadInput(e.target.validity.badInput);
        onChange(e);
      }}
      {...field}
    />
  );
}